import React, { useState, useMemo } from 'react';
import { ServerIcon, MagnifyingGlassIcon, ChevronDownIcon, ChevronUpIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const ConnectionHealthHeatmap = ({ connections = [], loading = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyFailing, setShowOnlyFailing] = useState(false);
  const [showAllHealthy, setShowAllHealthy] = useState(false);
  const navigate = useNavigate();

  // Get health score color
  const getHealthColor = (score) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 75) return 'bg-green-400';
    if (score >= 60) return 'bg-yellow-400';
    if (score >= 40) return 'bg-orange-400';
    return 'bg-red-500';
  };

  const getHealthTextColor = (score) => {
    if (score >= 90) return 'text-green-700';
    if (score >= 75) return 'text-green-600';
    if (score >= 60) return 'text-yellow-700';
    if (score >= 40) return 'text-orange-700';
    return 'text-red-700';
  };

  const getHealthBorderColor = (score) => {
    if (score >= 90) return 'border-green-200';
    if (score >= 75) return 'border-green-300';
    if (score >= 60) return 'border-yellow-300';
    if (score >= 40) return 'border-orange-300';
    return 'border-red-300';
  };

  const getHealthBgColor = (score) => {
    if (score >= 90) return 'bg-green-50';
    if (score >= 75) return 'bg-green-50';
    if (score >= 60) return 'bg-yellow-50';
    if (score >= 40) return 'bg-orange-50';
    return 'bg-red-50';
  };

  const getHealthLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 75) return 'Good';
    if (score >= 60) return 'Fair';
    if (score >= 40) return 'Poor';
    return 'Critical';
  };

  // Flatten all tables with connection info
  const allTables = useMemo(() => {
    const tables = [];
    connections.forEach(conn => {
      if (conn.tables && conn.tables.length > 0) {
        conn.tables.forEach(table => {
          tables.push({
            ...table,
            connectionId: conn.id,
            connectionName: conn.name,
            connectionType: conn.type
          });
        });
      }
    });
    return tables;
  }, [connections]);

  // Filter and sort tables
  const filteredTables = useMemo(() => {
    let filtered = allTables;

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(table =>
        table.name.toLowerCase().includes(search) ||
        table.connectionName.toLowerCase().includes(search)
      );
    }

    // Apply failing filter
    if (showOnlyFailing) {
      filtered = filtered.filter(table => table.health_score < 80);
    }

    // Sort by health score (worst first)
    return filtered.sort((a, b) => a.health_score - b.health_score);
  }, [allTables, searchTerm, showOnlyFailing]);

  // Split into failing and healthy
  const failingTables = filteredTables.filter(t => t.health_score < 80);
  const healthyTables = filteredTables.filter(t => t.health_score >= 80);

  // Show top 10 failing by default
  const displayedFailingTables = failingTables;
  const displayedHealthyTables = showAllHealthy ? healthyTables : healthyTables.slice(0, 5);

  const TableRow = ({ table }) => {
    // Carry connectionId in the URL so the validations page lands on the
    // right connection's data instead of whatever's currently active.
    const buildHref = () => {
      const params = new URLSearchParams();
      params.set('table', table.name);
      if (table.connectionId) {
        params.set('connection_id', table.connectionId);
      }
      return `/validations?${params.toString()}`;
    };
    return (
    <div
      onClick={() => navigate(buildHref())}
      className={`
        p-4 rounded-lg border-2 cursor-pointer transition-all duration-200
        hover:shadow-md hover:border-blue-400
        ${getHealthBgColor(table.health_score)} ${getHealthBorderColor(table.health_score)}
      `}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <ServerIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
            <h4 className="font-semibold text-gray-900 truncate">{table.name}</h4>
          </div>
          <p className="text-xs text-gray-600 truncate">
            {table.connectionName}
            {table.total_validations > 0 && (
              <span className="ml-2">
                • {table.passing_count}/{table.total_validations} passing
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-3 ml-4 flex-shrink-0">
          {table.total_validations > 0 ? (
            <>
              <div className="text-right">
                <div className={`text-2xl font-bold ${getHealthTextColor(table.health_score)}`}>
                  {Math.round(table.health_score)}%
                </div>
                <div className="text-xs text-gray-500">{getHealthLabel(table.health_score)}</div>
              </div>
              <div className={`w-3 h-3 rounded-full ${getHealthColor(table.health_score)}`} />
            </>
          ) : (
            <div className="text-xs text-gray-500">No validations</div>
          )}
        </div>
      </div>

      {table.failing_count > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <span className="text-xs font-medium text-red-600">
            {table.failing_count} validation{table.failing_count !== 1 ? 's' : ''} failing
          </span>
        </div>
      )}
    </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="mb-6">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-2 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, idx) => (
            <div key={idx} className="p-4 rounded-lg border-2 border-gray-200 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="h-4 w-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                  <div className="text-right">
                    <div className="h-8 bg-gray-200 rounded w-12 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-16"></div>
                  </div>
                  <div className="w-3 h-3 rounded-full bg-gray-200" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!connections || connections.length === 0 || allTables.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <ServerIcon className="h-12 w-12 mb-2" />
          <p className="text-sm">No table data available</p>
          <p className="text-xs mt-1">Connect a data source and run validations to see table health</p>
        </div>
      </div>
    );
  }

  if (filteredTables.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {/* Header */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Connection Health Overview</h3>
          <p className="text-sm text-gray-600 mt-1">Table validation status across all connections</p>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search tables or connections..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={() => setShowOnlyFailing(!showOnlyFailing)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              showOnlyFailing
                ? 'bg-red-100 text-red-700 border-2 border-red-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
            }`}
          >
            {showOnlyFailing ? 'Show All' : 'Failing Only'}
          </button>
        </div>

        <div className="flex flex-col items-center justify-center h-32 text-gray-500">
          <p className="text-sm">No tables match your filters</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Connection Health Overview</h3>
        <p className="text-sm text-gray-600 mt-1">
          {allTables.length} tables • {failingTables.length} requiring attention
        </p>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search tables or connections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          onClick={() => setShowOnlyFailing(!showOnlyFailing)}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex-shrink-0 ${
            showOnlyFailing
              ? 'bg-red-100 text-red-700 border-2 border-red-300'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
          }`}
        >
          {showOnlyFailing ? 'Show All' : 'Failing Only'}
        </button>
      </div>

      {/* Failing Tables Section */}
      {failingTables.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <h4 className="text-md font-semibold text-gray-900">
              Tables Requiring Attention ({failingTables.length})
            </h4>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
            {displayedFailingTables.map((table, index) => (
              <TableRow key={`${table.connectionId}-${table.name}-${index}`} table={table} />
            ))}
          </div>
        </div>
      )}

      {/* Healthy Tables Section */}
      {!showOnlyFailing && healthyTables.length > 0 && (
        <div>
          <button
            onClick={() => setShowAllHealthy(!showAllHealthy)}
            className="flex items-center gap-2 mb-3 text-gray-700 hover:text-gray-900 transition-colors"
          >
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <h4 className="text-md font-semibold">
              Healthy Tables ({healthyTables.length})
            </h4>
            {showAllHealthy ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>

          {showAllHealthy && (
            <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
              {displayedHealthyTables.map((table, index) => (
                <TableRow key={`${table.connectionId}-${table.name}-${index}`} table={table} />
              ))}
            </div>
          )}

          {!showAllHealthy && displayedHealthyTables.length > 0 && (
            <div className="space-y-2">
              {displayedHealthyTables.slice(0, 3).map((table, index) => (
                <TableRow key={`${table.connectionId}-${table.name}-${index}`} table={table} />
              ))}
              {healthyTables.length > 3 && (
                <button
                  onClick={() => setShowAllHealthy(true)}
                  className="w-full py-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Show {healthyTables.length - 3} more healthy tables...
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* All Green State */}
      {failingTables.length === 0 && !showOnlyFailing && (
        <div className="text-center py-8 bg-green-50 rounded-lg border-2 border-green-200">
          <CheckBadgeIcon className="h-12 w-12 mx-auto mb-2 text-green-600" />
          <h4 className="text-lg font-semibold text-green-900 mb-1">All Systems Green!</h4>
          <p className="text-sm text-green-700">All tables are passing their validations</p>
        </div>
      )}
    </div>
  );
};

export default ConnectionHealthHeatmap;
